import { useState } from 'react';
import { ScraperStatus } from '../api/adminApi';
import styles from './HealthDot.module.css';

type HealthLevel = 'green' | 'amber' | 'red';

function getHealth(status: ScraperStatus | null): HealthLevel {
  if (!status || status.runCount === 0) return 'amber';
  if (status.lastRunError) return 'red';
  if (!status.lastRunAt) return 'amber';
  const ageMs = Date.now() - new Date(status.lastRunAt).getTime();
  // During the decision window the scraper runs every 1h; outside every 24h.
  // Use proportional thresholds: amber at 2× expected interval, red at 4×.
  const inWindow = (status.lastRunResult?.locationsScraped?.length ?? 0) > 0;
  const expectedMs = inWindow ? 3_600_000 : 24 * 3_600_000;
  if (ageMs < expectedMs * 2) return 'green';
  if (ageMs < expectedMs * 4) return 'amber';
  return 'red';
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) {
    const abs = Math.abs(diff);
    if (abs < 60_000) return 'in <1 min';
    if (abs < 3_600_000) return `in ${Math.round(abs / 60_000)}m`;
    return `in ${Math.round(abs / 3_600_000)}h`;
  }
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  return `${Math.round(diff / 86_400_000)}d ago`;
}

interface Props {
  status: ScraperStatus | null;
}

export default function HealthDot({ status }: Props) {
  const [open, setOpen] = useState(false);
  const health = getHealth(status);
  const result = status?.lastRunResult;

  const summary = (() => {
    if (!status || status.runCount === 0) return 'Scraper has not run yet';
    if (status.isRunning) return 'Scraper is running now…';
    if (status.lastRunError) return `Last run failed: ${status.lastRunError}`;
    if (!result) return `Last run ${formatRelative(status.lastRunAt)}`;
    const scraped = result.locationsScraped;
    const skipped = Object.keys(result.skippedReasons);
    const parts: string[] = [];
    if (result.detected > 0) parts.push(`${result.detected} detection${result.detected > 1 ? 's' : ''} found`);
    else parts.push('nothing found');
    if (scraped.length) parts.push(`scraped ${scraped.join(', ')}`);
    if (skipped.length) parts.push(`${skipped.length} skipped (not day 28-29)`);
    return `${formatRelative(status.lastRunAt)} — ${parts.join(', ')}`;
  })();

  return (
    <div className={styles.wrap}>
      <button
        className={`${styles.dot} ${styles[health]}`}
        onClick={() => setOpen((v) => !v)}
        title="Scraper health"
        aria-label="Scraper health"
      >
        <span className={styles.dotInner} />
      </button>

      {open && (
        <>
          <div className={styles.backdrop} onClick={() => setOpen(false)} />
          <div className={styles.drawer}>
            <div className={styles.drawerHeader}>
              <span className={styles.drawerTitle}>Scraper Health</span>
              <button className={styles.drawerClose} onClick={() => setOpen(false)}>
                <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
                  <path d="M3.72 3.72a.75.75 0 011.06 0L8 6.94l3.22-3.22a.75.75 0 111.06 1.06L9.06 8l3.22 3.22a.75.75 0 11-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 01-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 010-1.06z" />
                </svg>
              </button>
            </div>

            <p className={styles.summary}>{summary}</p>

            <div className={styles.grid}>
              <DrawerCell label="State">
                {status?.isRunning
                  ? <span className={styles.running}><span className={styles.pulse} />Running</span>
                  : <span className={styles.idle}>Idle</span>}
              </DrawerCell>
              <DrawerCell label="Last run">
                <span title={status?.lastRunAt ?? undefined}>{formatRelative(status?.lastRunAt ?? null)}</span>
              </DrawerCell>
              <DrawerCell label="Next run">
                <span title={status?.nextRunAt ?? undefined}>{formatRelative(status?.nextRunAt ?? null)}</span>
              </DrawerCell>
              <DrawerCell label="Total runs"><span>{status?.runCount ?? 0}</span></DrawerCell>
            </div>

            {result && Object.keys(result.skippedReasons).length > 0 && (
              <div className={styles.skipped}>
                <p className={styles.skippedTitle}>Skipped (not in sighting window)</p>
                {Object.entries(result.skippedReasons).map(([code, reason]) => (
                  <div key={code} className={styles.skippedRow}>
                    <strong>{code}</strong><span>{reason}</span>
                  </div>
                ))}
              </div>
            )}

            {status?.lastRunError && (
              <div className={styles.errorBox}>{status.lastRunError}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DrawerCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.cell}>
      <span className={styles.cellLabel}>{label}</span>
      <span className={styles.cellValue}>{children}</span>
    </div>
  );
}
